import { useState, useEffect, useCallback } from 'react'
import { ShieldCheck, Shield, Briefcase, Calculator, Eye, Plus, Trash2, Settings, History, Check, X, RefreshCw } from 'lucide-react'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import api from '../lib/api'

// ── Types ──────────────────────────────────────────────────────────
interface User {
  id: string; email: string; firstName: string; lastName: string
  role: string; createdAt: string; lastLoginAt?: string; isActive?: boolean
}
interface PermMatrix {
  permissions: string[]
  matrix: Array<User & { effective: Record<string, { granted: boolean; source: 'role' | 'override' }> }>
}
interface AuditEntry {
  id: string; action: string; resource: string | null; resourceId: string | null
  userEmail: string | null; details: string | null; ipAddress: string | null; createdAt: string
}

// ── Constants ─────────────────────────────────────────────────────
const ROLES = [
  { value: 'super_admin', label: 'Super Administrateur', description: 'Accès total, gestion des comptes', color: 'bg-red-100 text-red-700',    icon: <ShieldCheck size={13} />, adminOnly: true },
  { value: 'admin',       label: 'Administrateur',       description: 'Accès complet, paramétrage',       color: 'bg-purple-100 text-purple-700', icon: <Shield size={13} />,      adminOnly: false },
  { value: 'manager',     label: 'Manager',              description: 'Planning, absences, employés',     color: 'bg-blue-100 text-blue-700',    icon: <Briefcase size={13} />,   adminOnly: false },
  { value: 'payroll',     label: 'Responsable Paie',     description: 'Paie, exports, lecture planning',  color: 'bg-green-100 text-green-700',  icon: <Calculator size={13} />,  adminOnly: false },
  { value: 'viewer',      label: 'Consultation',         description: 'Lecture seule',                    color: 'bg-gray-100 text-gray-600',    icon: <Eye size={13} />,         adminOnly: false },
]
const getRoleMeta = (role: string) => ROLES.find(r => r.value === role) ?? { value: role, label: role, description: '', color: 'bg-gray-100 text-gray-600', icon: null, adminOnly: false }

const PERM_LABELS: Record<string, { label: string; desc: string }> = {
  view_planning:       { label: 'Voir planning',           desc: 'Consulter les plannings publiés' },
  edit_planning:       { label: 'Modifier planning',       desc: 'Créer et éditer des shifts' },
  publish_planning:    { label: 'Publier planning',        desc: 'Publier les plannings aux employés' },
  view_employees:      { label: 'Voir employés',           desc: 'Accéder à la liste des employés' },
  edit_employees_hr:   { label: 'Modifier fiches RH',      desc: 'Éditer les données RH des employés' },
  view_salaries:       { label: 'Voir salaires',           desc: 'Accéder aux taux horaires et bruts' },
  export_payroll:      { label: 'Exporter paie',           desc: 'Générer et télécharger les exports paie' },
  manage_integrations: { label: 'Gérer intégrations',      desc: 'Configurer les connecteurs paie' },
  access_assistant:    { label: 'Assistant RH',            desc: 'Utiliser l\'assistant RH IA' },
  archive_employee:    { label: 'Archiver salarié',        desc: 'Archiver ou restaurer un salarié' },
  send_signature:      { label: 'Envoyer en signature',    desc: 'Demander la signature électronique' },
}
const PERM_GROUPS = [
  { label: 'Planning',    perms: ['view_planning','edit_planning','publish_planning'] },
  { label: 'Employés',    perms: ['view_employees','edit_employees_hr','archive_employee'] },
  { label: 'Paie',        perms: ['view_salaries','export_payroll','manage_integrations'] },
  { label: 'Outils',      perms: ['access_assistant','send_signature'] },
]

const EMPTY = { email: '', password: '', firstName: '', lastName: '', role: 'manager' }

// ── Component ─────────────────────────────────────────────────────
export default function Users() {
  const [tab, setTab]       = useState<'users'|'matrix'|'audit'>('users')
  const [users, setUsers]   = useState<User[]>([])
  const [matrix, setMatrix] = useState<PermMatrix | null>(null)
  const [auditLogs, setAuditLogs] = useState<AuditEntry[]>([])
  const [loading, setLoading] = useState(false)

  // User modal
  const [modal, setModal]   = useState<{ open: boolean; user?: User }>({ open: false })
  const [form, setForm]     = useState(EMPTY)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState('')

  // Permission edit
  const [editPerms, setEditPerms]   = useState<string | null>(null) // userId
  const [pendingPerms, setPendingPerms] = useState<Record<string, boolean>>({})
  const [savingPerms, setSavingPerms]   = useState(false)

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}')
  const isSuperAdmin = currentUser.role === 'super_admin'
  const isAdminUp    = isSuperAdmin || currentUser.role === 'admin'

  const loadUsers = useCallback(async () => {
    const r = await api.get('/users')
    setUsers(r.data)
  }, [])

  const loadMatrix = useCallback(async () => {
    setLoading(true)
    const r = await api.get('/permissions/matrix')
    setMatrix(r.data)
    setLoading(false)
  }, [])

  const loadAudit = useCallback(async () => {
    setLoading(true)
    const r = await api.get('/audit?limit=100')
    setAuditLogs(r.data)
    setLoading(false)
  }, [])

  useEffect(() => { loadUsers() }, [loadUsers])
  useEffect(() => {
    if (tab === 'matrix') loadMatrix()
    if (tab === 'audit')  loadAudit()
  }, [tab, loadMatrix, loadAudit])

  // ── User CRUD ────────────────────────────────────────────────────
  async function handleSave(e: React.FormEvent) {
    e.preventDefault(); setFormError(''); setSaving(true)
    try {
      if (modal.user) {
        const data: any = { firstName: form.firstName, lastName: form.lastName, role: form.role }
        if (form.password) data.password = form.password
        await api.put(`/users/${modal.user.id}`, data)
      } else {
        await api.post('/users', form)
      }
      await loadUsers(); setModal({ open: false })
    } catch (err: any) { setFormError(err.response?.data?.error || 'Erreur') }
    setSaving(false)
  }

  async function handleDelete(u: User) {
    if (!confirm(`Supprimer ${u.firstName} ${u.lastName} ?`)) return
    await api.delete(`/users/${u.id}`); loadUsers()
  }

  // ── Permission edit ──────────────────────────────────────────────
  function startEditPerms(u: any) {
    const initial: Record<string, boolean> = {}
    for (const [perm, data] of Object.entries(u.effective as Record<string, any>)) {
      initial[perm] = data.granted
    }
    setPendingPerms(initial); setEditPerms(u.id)
  }

  async function savePerms(userId: string) {
    setSavingPerms(true)
    await api.put(`/permissions/users/${userId}`, { permissions: pendingPerms })
    setSavingPerms(false); setEditPerms(null); loadMatrix()
  }

  async function resetPerms(userId: string) {
    if (!confirm('Réinitialiser toutes les permissions aux valeurs du rôle ?')) return
    await api.delete(`/permissions/users/${userId}/reset`); loadMatrix()
  }

  const availableRoles = ROLES.filter(r => isSuperAdmin || !r.adminOnly)

  return (
    <div className="p-6 space-y-5 max-w-6xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Utilisateurs & Sécurité</h1>
          <p className="text-sm text-gray-500 mt-0.5">Gestion des comptes, permissions et journal d'audit</p>
        </div>
        {isAdminUp && tab === 'users' && (
          <button onClick={() => { setForm(EMPTY); setFormError(''); setModal({ open: true }) }}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
            <Plus size={14} /> Ajouter un utilisateur
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {[['users','Utilisateurs'],['matrix','Matrice des droits'],['audit','Journal d\'audit']] .map(([t, lbl]) => (
          <button key={t} onClick={() => setTab(t as any)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === t ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {t === 'matrix' && <Settings size={14} />}
            {t === 'audit'  && <History size={14} />}
            {lbl}
          </button>
        ))}
      </div>

      {/* ── Tab: Utilisateurs ─────────────────────────────────────── */}
      {tab === 'users' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {ROLES.map(r => (
              <div key={r.value} className="bg-white border border-gray-200 rounded-xl p-3">
                <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${r.color} mb-1`}>
                  {r.icon}{r.label}
                </span>
                <p className="text-xs text-gray-500 leading-snug">{r.description}</p>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['Nom','Email','Rôle','Dernière connexion','Statut',''].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map(u => {
                  const meta = getRoleMeta(u.role)
                  return (
                    <tr key={u.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                            {u.firstName[0]}{u.lastName[0]}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{u.firstName} {u.lastName}</p>
                            {u.id === currentUser.id && <span className="text-xs text-gray-400">Vous</span>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{u.email}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${meta.color}`}>
                          {meta.icon}{meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {u.lastLoginAt ? format(new Date(u.lastLoginAt), "d MMM yyyy HH:mm", { locale: fr }) : 'Jamais'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${u.isActive !== false ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'}`}>
                          {u.isActive !== false ? 'Actif' : 'Inactif'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {isAdminUp && (isSuperAdmin || u.role !== 'super_admin') && (
                          <div className="flex items-center gap-1 justify-end">
                            <button onClick={() => { setForm({ email: u.email, password: '', firstName: u.firstName, lastName: u.lastName, role: u.role }); setFormError(''); setModal({ open: true, user: u }) }}
                              className="text-blue-600 hover:bg-blue-50 text-xs px-2 py-1 rounded transition-colors">Modifier</button>
                            {u.id !== currentUser.id && (
                              <button onClick={() => handleDelete(u)}
                                className="text-red-500 hover:bg-red-50 text-xs px-2 py-1 rounded transition-colors">Supprimer</button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Tab: Matrice des droits ───────────────────────────────── */}
      {tab === 'matrix' && (
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-12 text-gray-400">Chargement…</div>
          ) : matrix && (
            <div className="space-y-6">
              {matrix.matrix.map(u => {
                const meta = getRoleMeta(u.role)
                const isEditing = editPerms === u.id
                const perms = isEditing ? pendingPerms : Object.fromEntries(
                  Object.entries(u.effective).map(([p, d]) => [p, d.granted])
                )
                return (
                  <div key={u.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gray-50">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white text-xs font-bold">
                          {u.firstName[0]}{u.lastName[0]}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900 text-sm">{u.firstName} {u.lastName}</p>
                          <p className="text-xs text-gray-400">{u.email}</p>
                        </div>
                        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${meta.color}`}>
                          {meta.icon}{meta.label}
                        </span>
                        {u.role === 'super_admin' && (
                          <span className="text-xs text-gray-400 italic">Tous les droits (non modifiable)</span>
                        )}
                      </div>
                      {isAdminUp && u.role !== 'super_admin' && (
                        <div className="flex items-center gap-2">
                          {isEditing ? (
                            <>
                              <button onClick={() => savePerms(u.id)} disabled={savingPerms}
                                className="flex items-center gap-1 text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50">
                                <Check size={12} /> Enregistrer
                              </button>
                              <button onClick={() => setEditPerms(null)}
                                className="text-xs text-gray-500 border border-gray-200 hover:bg-gray-100 px-3 py-1.5 rounded-lg transition-colors">
                                Annuler
                              </button>
                            </>
                          ) : (
                            <>
                              <button onClick={() => startEditPerms(u)}
                                className="flex items-center gap-1 text-xs text-blue-600 border border-blue-200 hover:bg-blue-50 px-3 py-1.5 rounded-lg transition-colors">
                                <Settings size={12} /> Personnaliser
                              </button>
                              <button onClick={() => resetPerms(u.id)}
                                className="flex items-center gap-1 text-xs text-gray-500 border border-gray-200 hover:bg-gray-100 px-3 py-1.5 rounded-lg transition-colors">
                                <RefreshCw size={12} /> Réinitialiser
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    {u.role !== 'super_admin' && (
                      <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                        {PERM_GROUPS.map(group => (
                          <div key={group.label}>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{group.label}</p>
                            <div className="space-y-1.5">
                              {group.perms.map(perm => {
                                const granted = perms[perm] ?? false
                                const isOverride = u.effective[perm]?.source === 'override'
                                return (
                                  <div key={perm} className="flex items-center gap-2">
                                    {isEditing ? (
                                      <button type="button" onClick={() => setPendingPerms(p => ({ ...p, [perm]: !p[perm] }))}
                                        className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-colors ${granted ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-400'}`}>
                                        {granted ? <Check size={11} /> : <X size={11} />}
                                      </button>
                                    ) : (
                                      <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${granted ? 'bg-green-100 text-green-600' : 'bg-red-50 text-red-400'}`}>
                                        {granted ? <Check size={11} /> : <X size={11} />}
                                      </div>
                                    )}
                                    <span className={`text-xs ${granted ? 'text-gray-700' : 'text-gray-400'}`}>
                                      {PERM_LABELS[perm]?.label || perm}
                                    </span>
                                    {isOverride && !isEditing && (
                                      <span className="text-xs text-amber-500" title="Permission personnalisée">*</span>
                                    )}
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
          <p className="text-xs text-gray-400 text-center">* = permission personnalisée (différente du rôle par défaut)</p>
        </div>
      )}

      {/* ── Tab: Journal d'audit ──────────────────────────────────── */}
      {tab === 'audit' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">{auditLogs.length} entrées récentes</p>
            <button onClick={loadAudit} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 border border-gray-200 px-3 py-1.5 rounded-lg bg-white transition-colors">
              <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Actualiser
            </button>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['Date','Utilisateur','Action','Ressource','IP'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading && <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-400">Chargement…</td></tr>}
                {!loading && auditLogs.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-12 text-center text-gray-400">Aucune entrée d'audit</td></tr>
                )}
                {auditLogs.map(log => (
                  <tr key={log.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                      {format(new Date(log.createdAt), "d MMM yyyy HH:mm:ss", { locale: fr })}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-700">{log.userEmail || '—'}</td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">{log.action}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {log.resource && <span>{log.resource}{log.resourceId ? ` #${log.resourceId.slice(0,8)}` : ''}</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400 font-mono">{log.ipAddress || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Modal: Créer / Modifier utilisateur ──────────────────── */}
      {modal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={e => { if (e.target === e.currentTarget) setModal({ open: false }) }}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900">{modal.user ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}</h2>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Prénom *</label>
                  <input value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Nom *</label>
                  <input value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                </div>
              </div>
              {!modal.user && (
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Email *</label>
                  <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">
                  {modal.user ? 'Nouveau mot de passe (vide = inchangé)' : 'Mot de passe *'}
                </label>
                <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required={!modal.user} />
                {!modal.user && <p className="text-xs text-gray-400 mt-1">Min. 8 caractères, 1 majuscule, 1 chiffre</p>}
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Rôle</label>
                <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {ROLES.filter(r => isSuperAdmin || !r.adminOnly).map(r => (
                    <option key={r.value} value={r.value}>{r.label} — {r.description}</option>
                  ))}
                </select>
              </div>
              {formError && <p className="text-red-500 text-sm">{formError}</p>}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setModal({ open: false })}
                  className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-700 text-sm font-medium hover:bg-gray-50">
                  Annuler
                </button>
                <button type="submit" disabled={saving}
                  className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
                  {saving ? 'Enregistrement…' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
